import { useRef, useState, type ReactNode } from 'react';
import type { TradeRecord } from '@mypoz/shared';
import { clampPackagePage, packagePageCount } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, Icon, cn } from '@mypoz/ui';
import { Link } from 'react-router-dom';

import { Pager } from '@/components/Pager.js';
import { useFormat } from '@/lib/format.js';
import { actionLinkClass, buttonClass } from '@/lib/styles.js';
import { ORDERS_ROUTE } from '@/shell/navigation.js';
import { ReceiptMenu } from './ReceiptMenu.js';

/** The same page size as the top-up history beside it. */
const TRANSACTIONS_PER_PAGE = 20;

export interface TransactionsCardProps {
  status: 'loading' | 'ready' | 'error';
  records: readonly TradeRecord[];
  onRetry: () => void;
  /** The 账户明细 heading and tabs, shared with the top-up history. */
  header: ReactNode;
}

/**
 * The Wallet's transaction history (MYP-146): the old website's 账户明细 · 所有交易. Every
 * movement of the wallet's money that went through — order charges, credits, completed top-ups,
 * refunds — with the balance after each, so the balance can be followed step by step.
 *
 * A table from `lg`, as on the old website; on a phone each movement is a short card. Paged in the
 * browser, 20 to a page: the whole list is fetched once (see `fetchTradeRows`).
 */
export function TransactionsCard({ status, records, onRetry, header }: TransactionsCardProps) {
  const { t } = useTranslation();
  const [requestedPage, setRequestedPage] = useState(1);
  const top = useRef<HTMLDivElement>(null);

  const goToPage = (next: number) => {
    setRequestedPage(next);
    top.current?.scrollIntoView({ block: 'start' });
  };

  const pageCount = packagePageCount(records.length, TRANSACTIONS_PER_PAGE);
  const page = clampPackagePage(requestedPage, records.length, TRANSACTIONS_PER_PAGE);
  const pageStart = (page - 1) * TRANSACTIONS_PER_PAGE;
  const visible = records.slice(pageStart, pageStart + TRANSACTIONS_PER_PAGE);

  return (
    <div ref={top} className="grid scroll-mt-24 gap-4">
      <Card className="p-0">
        {header}

        {status === 'loading' ? (
          <p aria-busy="true" className="px-5 py-10 text-center text-sm text-muted sm:px-6">
            {t('wallet.transactions.loading')}
          </p>
        ) : status === 'error' ? (
          <div className="grid justify-items-center gap-3 px-5 py-10 sm:px-6">
            <p className="text-center text-sm text-ink">{t('wallet.transactions.failed')}</p>
            <button type="button" onClick={onRetry} className={buttonClass('secondary')}>
              <Icon name="refresh" size={18} />
              {t('actions.retry')}
            </button>
          </div>
        ) : records.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-muted sm:px-6">
            {t('wallet.transactions.empty')}
          </p>
        ) : (
          <>
            <TransactionsTable records={visible} />
            <ul className="divide-y divide-line lg:hidden">
              {visible.map((record) => (
                <TransactionItem key={record.id} record={record} />
              ))}
            </ul>
          </>
        )}
      </Card>

      {status === 'ready' && records.length > TRANSACTIONS_PER_PAGE ? (
        <Pager
          page={page}
          pageCount={pageCount}
          rangeStart={pageStart + 1}
          rangeEnd={pageStart + visible.length}
          total={records.length}
          onGo={goToPage}
          label={t('wallet.transactions.pagerLabel')}
        />
      ) : null}
    </div>
  );
}

/** Laptop: the old website's columns, in its order. */
function TransactionsTable({ records }: { records: readonly TradeRecord[] }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  // Headings, times, amounts and types never wrap; only the remark does. A narrow laptop scrolls
  // the table sideways inside the card rather than breaking a type one character per line.
  const head =
    'px-3 py-2.5 text-left text-xs font-semibold whitespace-nowrap text-muted first:pl-6 last:pr-6';
  const cell = 'px-3 py-3 align-top first:pl-6 last:pr-6';

  return (
    <div className="hidden overflow-x-auto lg:block">
      <table className="w-full text-sm">
        <thead className="border-b border-line bg-surface-alt">
          <tr>
            <th scope="col" className={head}>
              {t('wallet.transactions.columns.time')}
            </th>
            <th scope="col" className={head}>
              {t('wallet.transactions.columns.reference')}
            </th>
            <th scope="col" className={head}>
              {t('wallet.transactions.columns.bank')}
            </th>
            <th scope="col" className={cn(head, 'text-right')}>
              {t('wallet.transactions.columns.amount')}
            </th>
            <th scope="col" className={cn(head, 'text-right')}>
              {t('wallet.transactions.columns.balance')}
            </th>
            <th scope="col" className={head}>
              {t('wallet.transactions.columns.type')}
            </th>
            <th scope="col" className={head}>
              {t('wallet.transactions.columns.remark')}
            </th>
            <th scope="col" className={head}>
              {t('wallet.transactions.columns.receipt')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {records.map((record) => (
            <tr key={record.id}>
              <td className={cn(cell, 'whitespace-nowrap text-muted tabular-nums')}>
                {record.at ? fmt.dateTime(record.at) : '—'}
              </td>
              <td className={cn(cell, 'whitespace-nowrap tabular-nums')}>
                <Reference record={record} />
              </td>
              <td className={cn(cell, 'whitespace-nowrap text-muted')}>{record.bank ?? '—'}</td>
              <td className={cn(cell, 'text-right whitespace-nowrap')}>
                <Amount record={record} />
              </td>
              <td className={cn(cell, 'text-right whitespace-nowrap text-ink tabular-nums')}>
                {record.balanceCents === undefined
                  ? '—'
                  : fmt.money(record.balanceCents, record.currency)}
              </td>
              <td className={cn(cell, 'whitespace-nowrap text-ink')}>{record.typeLabel}</td>
              <td
                className={cn(
                  cell,
                  'text-muted',
                  // A one-word remark (the order number in brackets) stays on one line; prose wraps.
                  record.remark && /\s/.test(record.remark)
                    ? 'min-w-40 break-words'
                    : 'whitespace-nowrap',
                )}
              >
                {record.remark ?? ''}
              </td>
              <td className={cn(cell, 'whitespace-nowrap')}>
                <ReceiptMenu
                  reference={record.reference}
                  receiptUrl={record.receiptUrl}
                  pdfUrl={record.pdfUrl}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Phone: type and amount first — what happened and how much — then the rest underneath. */
function TransactionItem({ record }: { record: TradeRecord }) {
  const { t } = useTranslation();
  const fmt = useFormat();

  return (
    <li className="grid gap-1 px-5 py-4 sm:px-6">
      <div className="flex items-baseline justify-between gap-3">
        <p className="min-w-0 text-sm font-semibold text-ink">{record.typeLabel}</p>
        <Amount record={record} />
      </div>
      <p className="text-xs text-muted tabular-nums">
        {[record.at ? fmt.dateTime(record.at) : undefined, record.bank].filter(Boolean).join(' · ')}
      </p>
      {record.balanceCents === undefined ? null : (
        <p className="text-xs text-muted tabular-nums">
          {t('wallet.transactions.balanceAfter', {
            balance: fmt.money(record.balanceCents, record.currency),
          })}
        </p>
      )}
      {record.remark ? <p className="text-xs break-words text-muted">{record.remark}</p> : null}
      {/* The order number and the receipts are links, so they get a line of their own (44px). */}
      {record.reference || record.receiptUrl || record.pdfUrl ? (
        <div className="flex flex-wrap items-center gap-x-4">
          {record.reference ? (
            <span className="text-xs text-muted tabular-nums">
              <Reference record={record} />
            </span>
          ) : null}
          <ReceiptMenu
            reference={record.reference}
            receiptUrl={record.receiptUrl}
            pdfUrl={record.pdfUrl}
          />
        </div>
      ) : null}
    </li>
  );
}

/** Money in with a plus, in green; money out with the minus it already has. */
function Amount({ record }: { record: TradeRecord }) {
  const fmt = useFormat();
  const incoming = record.amountCents > 0;
  return (
    <span
      className={cn(
        'shrink-0 text-sm font-semibold tabular-nums',
        incoming ? 'text-success' : 'text-ink',
      )}
    >
      {incoming ? '+' : ''}
      {fmt.money(record.amountCents, record.currency)}
    </span>
  );
}

/** An order number opens that order, the way an order notification does; a top-up's is text. */
function Reference({ record }: { record: TradeRecord }) {
  if (!record.reference) return <>—</>;
  if (!record.isOrder) return <>{record.reference}</>;
  return (
    <Link
      to={`${ORDERS_ROUTE}?order=${encodeURIComponent(record.reference)}`}
      className={actionLinkClass()}
    >
      {record.reference}
    </Link>
  );
}
